/*
 * A campaign link's location (DECISIONS D27, GUARDRAILS G44; mpgadmin G148,
 * D100). A campaign for one location sends the client here with
 * ?store={id} beside ?c={token}. The id is taken out of the address before
 * anything reads it and kept for this tab only, then applied once the
 * tenant's own stores are known: if it is one of them it becomes the chosen
 * location, over the one picked last time (Lena, 24 Oct 2026: "the link's
 * location wins"; the client can still change it). Anything else is ignored
 * without a word. It only preselects: the API checks the booking for the
 * store it names.
 */
import type { Store } from '../types/location.types';
import { isDemoStore } from '../utils/demoMode';

export const LINK_STORE_KEY = 'mpg_link_store_v1';

/** A store's id as the API has it: decimal digits, no leading zero, at most 9. */
const ID = /^[1-9][0-9]{0,8}$/;

/**
 * Keeps `?store=` from the address, when it reads as an id, for this tab, and
 * answers the address without it (null when there was nothing to take out).
 * One that isn't an id is dropped and kept nowhere.
 */
export const captureCampaignStore = (search: string): string | null => {
  const params = new URLSearchParams(search);
  const id = params.get('store');
  if (id === null) return null;
  try {
    if (ID.test(id)) sessionStorage.setItem(LINK_STORE_KEY, id);
    else sessionStorage.removeItem(LINK_STORE_KEY);
  } catch {
    /* storage off: the site opens as it would without the link's location */
  }
  params.delete('store');
  const rest = params.toString();
  return rest ? `?${rest}` : '';
};

export const forgetLinkStore = (): void => {
  try {
    sessionStorage.removeItem(LINK_STORE_KEY);
  } catch {
    /* nothing kept */
  }
};

/**
 * Applies a kept id once the tenant's stores are in: the matching store
 * becomes `selectedStore` (its id a number, as the location overlay keeps it)
 * and is answered for the caller to show; null when nothing is kept, the id
 * isn't one of `stores` (another salon's, deactivated, a typo) or is the demo
 * store. The kept id is forgotten either way, so it applies once.
 */
export const applyLinkStore = (stores: Store[] | null | undefined): Store | null => {
  let id: string | null = null;
  try {
    id = sessionStorage.getItem(LINK_STORE_KEY);
  } catch {
    return null;
  }
  if (id === null) return null;
  forgetLinkStore();
  const match = (stores || []).find((s) => s && String(s.id) === id);
  if (!match || isDemoStore({ ...match, id: Number(match.id) })) return null;
  const store: Store = { ...match, id: Number(match.id) };
  try {
    localStorage.setItem('selectedStore', JSON.stringify(store));
  } catch {
    /* not kept: chosen for this page only */
  }
  return store;
};
